import { create } from 'zustand';

interface CallerCommunicationState {
  unreadCounts: Map<string, number>;
  newLocationCounts: Map<string, number>;
  currentlyViewingCallerLinkId: string | null;
  currentlyOnLocationsTab: boolean;

  setUnreadCounts: (counts: Map<string, number>) => void;
  incrementUnreadFor: (callerLinkId: string) => void;
  clearUnreadFor: (callerLinkId: string) => void;

  setNewLocationCounts: (counts: Map<string, number>) => void;
  incrementNewLocationFor: (callerLinkId: string) => void;
  clearAllNewLocations: () => void;

  setCurrentlyViewing: (callerLinkId: string | null) => void;
  setCurrentlyOnLocationsTab: (value: boolean) => void;

  getTotalUnreadCount: () => number;
  getTotalNewLocationCount: () => number;
  getCombinedTotal: () => number;

  reset: () => void;
}

export const useCallerCommunicationStore = create<CallerCommunicationState>(
  (set, get) => ({
    unreadCounts: new Map(),
    newLocationCounts: new Map(),
    currentlyViewingCallerLinkId: null,
    currentlyOnLocationsTab: false,

    setUnreadCounts: (counts) => {
      set({ unreadCounts: counts });
    },

    incrementUnreadFor: (callerLinkId) => {
      set((state) => {
        const next = new Map(state.unreadCounts);
        next.set(callerLinkId, (next.get(callerLinkId) ?? 0) + 1);
        return { unreadCounts: next };
      });
    },

    clearUnreadFor: (callerLinkId) => {
      set((state) => {
        if (!state.unreadCounts.has(callerLinkId)) return state;
        const next = new Map(state.unreadCounts);
        next.delete(callerLinkId);
        return { unreadCounts: next };
      });
    },

    setNewLocationCounts: (counts) => {
      set({ newLocationCounts: counts });
    },

    incrementNewLocationFor: (callerLinkId) => {
      set((state) => {
        const next = new Map(state.newLocationCounts);
        next.set(callerLinkId, (next.get(callerLinkId) ?? 0) + 1);
        return { newLocationCounts: next };
      });
    },

    clearAllNewLocations: () => {
      set((state) => {
        if (state.newLocationCounts.size === 0) return state;
        return { newLocationCounts: new Map() };
      });
    },

    setCurrentlyViewing: (callerLinkId) => {
      set({ currentlyViewingCallerLinkId: callerLinkId });
    },

    setCurrentlyOnLocationsTab: (value) => {
      set({ currentlyOnLocationsTab: value });
    },

    getTotalUnreadCount: () => {
      let total = 0;
      for (const count of get().unreadCounts.values()) {
        total += count;
      }
      return total;
    },

    getTotalNewLocationCount: () => {
      let total = 0;
      for (const count of get().newLocationCounts.values()) {
        total += count;
      }
      return total;
    },

    getCombinedTotal: () => {
      return get().getTotalUnreadCount() + get().getTotalNewLocationCount();
    },

    reset: () => {
      set({
        unreadCounts: new Map(),
        newLocationCounts: new Map(),
        currentlyViewingCallerLinkId: null,
        currentlyOnLocationsTab: false,
      });
    },
  }),
);
